import type { ClearEnvSecretsResult } from './envClearNotes';
import { generateUUID } from './uuid';
import type { ChartPayload } from '@/types';

// In production (when served from backend), use relative paths
// In development, use explicit backend URL
const getApiBaseUrl = (): string => {
  const viteEnv = (import.meta as any).env;
  
  // If VITE_BACKEND_BASE_URL is explicitly set, use it
  if (viteEnv?.VITE_BACKEND_BASE_URL !== undefined) {
    return viteEnv.VITE_BACKEND_BASE_URL;
  }
  
  // Check if we're running in development mode (Vite dev server)
  // In dev mode, frontend runs on port 3000 and backend on 8080
  if (viteEnv?.DEV === true || viteEnv?.MODE === 'development') {
    return 'http://localhost:8080';
  }
  
  // If in production mode (served from backend static files), use relative paths
  if (viteEnv?.PROD === true || viteEnv?.MODE === 'production') {
    return ''; // Empty string for relative paths
  }
  
  // Fallback: check if we're on localhost with a dev server port
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const port = window.location.port;
    // Common dev server ports: 3000, 3001, 5173 (Vite default)
    const devPorts = ['3000', '3001', '5173'];
    if ((hostname === 'localhost' || hostname === '127.0.0.1') && devPorts.includes(port)) {
      return 'http://localhost:8080';
    }
  }
  
  // Default for production builds served from backend
  return '';
};

const API_BASE_URL = getApiBaseUrl();

export interface ChatRequest {
  content: string;
  session_id?: string;
  language?: 'ru' | 'en';  // Language preference for LLM responses
  model_preference?: string;  // 'auto' or specific provider/model name
  // Multi-cluster routing (optional — omit for single-cluster default)
  cluster_mode?: 'single' | 'sequential' | 'parallel';
  cluster_ids?: string[];
  synthesis_mode?: 'synthesise' | 'concat';
  /**
   * STORY-524 / STORY-511: the turn's id (a UUID). The server registers the
   * turn (Stop, close and logout cancel it; a reload finds it) and, with
   * server_save, stores the answer under this id itself.
   */
  turn_id?: string;
  server_save?: boolean;
  /** The model name to label a stored answer that carries no usage. */
  model_label?: string;
}

/** STORY-524: a chat turn as the server reports it to its owner. */
export interface ChatTurnState {
  turn_id: string;
  session_id: string;
  state: 'running' | 'finishing' | 'done' | 'cancelled' | 'failed';
  since: string;
  saved: boolean;
}

/** STORY-511: how a cancel landed — 'already_done' when the answer already existed. */
export type TurnCancelStatus = 'cancelled' | 'already_done' | 'failed';

export interface MultiClusterMeta {
  multi_cluster: boolean;
  cluster_routing_mode: string;
  clusters: Array<{
    id: string;
    name: string;
    success: boolean;
    duration_seconds: number;
    error?: string | null;
  }>;
  total_duration_seconds: number;
}

export interface PaginationMeta {
  pagination_id: string;
  current_page: number;
  total_pages: number;
  total_count: number;
  page_size: number;
  continuation_key?: string;
  api_total_count?: number;
}

export interface CollectMoreResponse {
  new_items_count: number;
  total_count: number;
  total_pages: number;
  page_size: number;
  continuation_key?: string;
  api_total_count?: number;
  remaining?: number;
}

export interface TableData {
  columns: Array<{ key: string; label: string }>;
  rows: Record<string, any>[];
  pagination_id: string;
  total_count: number;
  total_pages: number;
  page_size: number;
  continuation_key?: string;
  api_total_count?: number;
  /** The API's list name ("problems", "entities", …) — picks the footer caption. */
  list_key?: string;
  /** STORY-337: the block of the message text that previews this table's
   *  first rows, verbatim — hidden in the web UI, which shows the table. */
  text_preview?: string;
}

/**
 * STORY-275 — an application alert rule the chat PROPOSED. The chat never saves
 * it: the card shows it, and only the user's confirmation posts `rule` to
 * /api/alerts/rules with the user's own session.
 */
export interface AlertRuleProposal {
  kind: 'product_alert_rule';
  rule: {
    name: string;
    /** Absent means a metric rule. An SLO rule has no condition or threshold. */
    rule_kind?: 'metric' | 'slo';
    metric_query: string;
    condition?: string | null;
    threshold?: number | null;
    severity: string;
    notification_channels: { channels: string[]; webhook_url?: string };
    evaluation_interval: number;
    description?: string | null;
    entity_selector?: string | null;
  };
  /** Fields the ask did not give, filled with the form's defaults. */
  defaults?: string[];
  condition_defaulted?: boolean;
}

export interface ChatResponse {
  content: string;
  tool_calls?: Array<{
    name: string;
    args: Record<string, any>;
  }>;
  error?: string;
  token_usage?: TokenUsage;
  context_used?: boolean;
  multi_cluster?: MultiClusterMeta | null;
  pagination?: PaginationMeta | null;
  table_data?: TableData | null;
  /** STORY-298: charts the web chat draws; the content keeps the text chart. */
  chart_data?: ChartPayload[] | null;
  alert_rule_proposal?: AlertRuleProposal | null;
  /** STORY-304: set when the answer IS an incident card. */
  incident_card?: IncidentCardMeta | null;
  cache_stats?: Record<string, any> | null;
  stats?: Record<string, any> | null;
  /** STORY-524: the request's turn_id, echoed. */
  turn_id?: string | null;
  /** STORY-524: true when the server stored this answer under turn_id. */
  saved?: boolean | null;
}

export interface MCPServerInfo {
  name: string;
  url: string;
  transport: string;
  status: string;
  tools?: Array<{
    name: string;
    description: string;
  }>;
}

export interface ConversationStats {
  session_id: string;
  turn_count: number;
  entities_tracked: number;
  created_at: number;
  last_activity: number;
  age_seconds: number;
}

// =================== Configuration Status ===================

export interface ConfigStatus {
  configured: boolean;
  credentials: {
    cluster_url: boolean;
    cluster_token: boolean;
    env_token: boolean;
    gigachat: boolean;
    ollama: boolean;
    openai: boolean;
    anthropic: boolean;
  };
  system_status: string;
  mcp_mode?: string;
  mcp_connected?: boolean;
  llm_available?: boolean;
  vault_enabled: boolean;
}

// =================== Vault Types ===================

export interface VaultStatus {
  vault_available: boolean;
  vault_enabled: boolean;
  vault_address?: string;
  vault_connected?: boolean;
  vault_authenticated?: boolean | null;  // null = unknown (server unreachable); false = token rejected
  cache_stats?: Record<string, any>;
  error?: string;
  message?: string;
}

export interface SecretsListResponse {
  mcp: {
    cluster_base_url: string;
    cluster_api_token: string;
    environment_api_token: string;
  };
  chat: {
    gigachat_credentials: string;
    gigachat_model: string;
    gigachat_scope: string;
    mcp_server_url: string;
  };
  ollama: {
    enabled: string;
    base_url: string;
    models: string;
    default_model: string;
    instances: string;
  };
  email: {
    smtp_verify_email: string;   // unmasked — "true" | "false"
    smtp_host: string;
    smtp_port: string;
    smtp_user: string;
    smtp_password: string;
    smtp_from: string;
    smtp_use_tls: string;        // unmasked — "true" | "false"
    app_base_url: string;
  };
  telegram: {
    bot_token: string;
  };
  source: 'vault' | 'environment';
  vault_enabled: boolean;
  vault_address?: string;
}

export interface SetSecretResponse {
  status: 'success' | 'partial' | 'error';
  message: string;
  reload_results?: Record<string, {
    method: string;
    success: boolean;
    message: string;
  }>;
  all_services_reloaded?: boolean;
}

export interface ReloadResponse {
  status: 'success' | 'partial' | 'error';
  message: string;
  results?: Record<string, {
    method: string;
    success: boolean;
    message: string;
  }>;
  all_success?: boolean;
  llm_reinitialized?: boolean;
}

export interface CertificateStatus {
  has_certificate: boolean;
  source?: 'file' | 'vault';
  file_path?: string;
  valid: boolean;
  expired: boolean;
  expiry_date?: string;
  days_remaining?: number;
  max_api_calls?: number;
  constraint_type?: string;
  error?: string;
  /** STORY-485: the MCP's own answer (/health) for the certificate it runs */
  mcp_status?: 'active' | 'inactive' | 'other' | 'unknown';
  mcp_refusal?: string | null;
}

export interface CertificateUploadResponse {
  status: 'success' | 'error';
  storage: 'file' | 'vault';
  path?: string;
  expiry_date?: string;
  days_remaining?: number;
}

// =================== Extension Certificate Types ===================

export interface ExtensionCertificate {
  cert_id: string;
  base_cert_id?: string;
  extension_type?: string;
  new_api_limit?: number;
  extension_days?: number;
  valid_from?: string;
  valid_until?: string;
  // From the MCP server (STORY-477): 'active' only when it applies the
  // extension; 'unknown' when it did not answer.
  status: 'active' | 'inactive' | 'unknown' | 'revoked' | 'error' | 'missing_pem';
  days_remaining?: number;
  pem_exists?: boolean;
  error?: string;
}

export interface ExtensionCertListResponse {
  certificates: ExtensionCertificate[];
  count: number;
}

export interface ExtensionCertUploadResponse {
  status: 'success' | 'error';
  cert_id: string;
  valid_until: string;
  days_remaining: number;
  constraints: Record<string, any>;
  mcp_status: 'active' | 'inactive' | 'unknown';
}

export interface ExtensionCertRevokeResponse {
  status: 'revoked';
  cert_id: string;
  moved_files: string[];
}

// =================== Token Statistics Types ===================

export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  estimated_cost_usd?: number;
  provider?: string;
  model?: string;
}

export interface UsageSummary {
  total_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  avg_tokens_per_request: number;
}

export interface DailyUsageRecord {
  date: string;
  provider: string;
  model: string;
  total_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
}

export interface ProviderUsage {
  provider: string;
  total_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
}

export interface ModelUsage {
  model: string;
  provider: string;
  total_requests: number;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
}

export interface QuickSummary {
  today: UsageSummary;
  week: UsageSummary;
  month: UsageSummary;
  all_time: UsageSummary;
}

export interface TokenStatsResponse {
  records: DailyUsageRecord[];
  summary: UsageSummary;
}

export interface TokenStatsParams {
  start?: string;
  end?: string;
  provider?: string;
  model?: string;
}

// =================== Auth Types ===================

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user_id: number;
  username: string;
  email: string;
  role: string;
}

export interface RegisterResponse {
  message: string;
  user_id: number;
  username: string;
  email_verification_required: boolean;
}

export interface MessageResponse {
  message: string;
}

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  email_verified: boolean;
  created_at: string;
  last_login: string | null;
}

// =================== Admin Types ===================

export interface AdminUserView {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  email_verified: boolean;
  created_at: string;
  last_login: string | null;
  deactivated_at: string | null;
  deactivated_by: number | null;
  token_budget_monthly: number | null;
}

export interface AdminCreateUserRequest {
  username: string;
  email: string;
  password: string;
  role: string;
  token_budget_monthly?: number | null;
}

export interface AdminUserUsageView {
  user_id: number | null;
  username: string | null;
  total_requests: number;
  total_tokens: number;
  total_cost_usd: number;
  token_budget_monthly: number | null;
}

export interface AdminUserUsageResponse {
  period: string;
  users: AdminUserUsageView[];
}

export interface AdminUserListResponse {
  users: AdminUserView[];
  total: number;
  page: number;
  page_size: number;
}

// ── Dispatch overrides (STORY-227/228) ───────────────────────────────────────
export type OverrideScope = 'recipe_pattern' | 'recipe_description' | 'specialist_addendum';

export interface DispatchOverrideView {
  id: number;
  scope: OverrideScope;
  target: string;
  language: string | null;
  payload: string;
  enabled: boolean;
}

export interface DispatchOverrideCreate {
  scope: OverrideScope;
  target: string;
  payload: string;
  language?: string | null;
}

export interface DispatchMatchDetail {
  recipe: string | null;
  pattern: string | null;
  specificity: number | null;
  is_custom: boolean;
  tier: string | null;
  blocked_by_guard: string[];
}

export interface DispatchDryRunResult {
  query: string;
  language: string;
  before: string | null;
  before_detail: DispatchMatchDetail;
  after?: string | null;
  after_detail?: DispatchMatchDetail;
  validation?: { ok: boolean; reason: string; [k: string]: any };
}

export interface DispatchOverrideTargets {
  recipes: string[];
  recipe_descriptions: Record<string, string>;
  specialists: string[];
  scopes: OverrideScope[];
  languages: string[];
}

// ── Knowledge base (STORY-164 / STORY-303) ───────────────────────────────────

export type KbScope = 'shared' | 'personal';

export interface KbDocument {
  id: number;
  title: string;
  source: string;
  cluster: string | null;
  chunks: number;
  scope: KbScope;
  /** True when the caller owns the document. */
  own: boolean;
  created_at: string | null;
}

export interface KbIngestRequest {
  title: string;
  content: string;
  source?: string;
  cluster?: string | null;
  scope?: KbScope;
}

export interface KbIngestResponse {
  id: number;
  title: string;
  chunks: number;
  scope: KbScope;
  owner_id: number;
  redacted_kinds: string[];
}

export interface KbSearchHit {
  content: string;
  seq: number;
  source: string;
  title: string;
  origin: string;
  document_id: number;
  score: number;
}

/**
 * A document refused at ingest because it carries a secret. Only the finding
 * KINDS come back — the backend never echoes the content (D7).
 */
export class KbIngestBlockedError extends Error {
  findings: string[];

  constructor(findings: string[]) {
    super('ingest_blocked');
    this.name = 'KbIngestBlockedError';
    this.findings = findings;
  }
}

export interface AdminActionResponse {
  message: string;
  user_id: number;
  is_active: boolean;
}

export interface ThresholdUpdate {
  metric_key: string;
  entity_pattern?: string;
  threshold_value: number;
  threshold_type: 'upper' | 'lower';
}

export interface ThresholdResponse {
  id: number;
  metric_key: string;
  entity_pattern: string | null;
  threshold_value: number;
  threshold_type: string;
}

// =================== Alert Types (Sprint 24) ===================

export interface AlertRuleResponse {
  id: number;
  name: string;
  /** ``metric`` (default) or ``slo``. Missing on older payloads means metric. */
  rule_kind?: 'metric' | 'slo';
  metric_query: string;
  condition: string | null;
  threshold: number | null;
  severity: string;
  notification_channels: Record<string, any> | null;
  evaluation_interval: number;
  is_enabled: boolean;
  description: string | null;
  entity_selector: string | null;
  last_evaluated_at: string | null;
  last_skip_reason?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AlertEventResponse {
  id: number;
  rule_id: number;
  rule_name: string | null;
  status: string;
  current_value: number | null;
  entity_id: string | null;
  entity_name: string | null;
  message: string | null;
  condition: string | null;
  threshold: number | null;
  // when the rule was last changed; condition/threshold are the rule's current ones
  rule_updated_at?: string | null;
  notified: boolean;
  notified_at: string | null;
  fired_at: string;
  resolved_at: string | null;
}

export interface AlertSummaryResponse {
  total_rules: number;
  enabled_rules: number;
  total_events: number;
  firing: number;
  resolved: number;
  by_severity: Record<string, number>;
}

// =================== Scheduled Queries Types ===================

export interface ScheduledQueryResponse {
  id: number;
  name: string;
  query_text: string | null;
  scenario_id: string | null;
  cron_expression: string;
  is_active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  last_result: string | null;
  last_status: string;
  notification_method: string;
  created_at: string;
  updated_at: string;
}

export interface ScenarioSummary {
  id: string;
  title: string;
  description: string;
}

export interface ScheduledQueryCreateBody {
  name: string;
  query_text?: string | null;
  scenario_id?: string | null;
  cron_expression: string;
  notification_method?: string;
}

export interface ScheduledQueryUpdateBody {
  name?: string;
  query_text?: string | null;
  scenario_id?: string | null;
  cron_expression?: string;
  is_active?: boolean;
  notification_method?: string;
}

export interface ScheduledQueryResultResponse {
  id: number;
  query_id: number;
  result_text: string | null;
  status: string;
  executed_at: string;
  duration_ms: number | null;
}

// =================== Telegram Types ===================

export interface TelegramLinkStatusResponse {
  is_linked: boolean;
  is_active: boolean;
  telegram_username: string | null;
  telegram_chat_id: number | null;
  created_at: string | null;
}

export interface TelegramTokenGenerateResponse {
  access_token: string;
  bot_username: string | null;
  deep_link: string | null;
  qr_code_data_url: string | null;
  instructions: string;
}

export interface TelegramBotInfoResponse {
  is_configured: boolean;
  bot_username: string | null;
}

/** STORY-304: the backend's marker on an answer that is an incident card. */
export interface IncidentCardMeta {
  problem_id: string;
  generated_at: string;
}

export interface SendCardResponse {
  status: string;
  parts: number;
}

// =================== Analytics Types ===================

export interface SessionCostResponse {
  session_id: string;
  total_prompt_tokens: number;
  total_completion_tokens: number;
  total_tokens: number;
  total_cost_rub: number;
  request_count: number;
  models_used: string[];
  avg_tokens_per_request: number;
}

export interface ToolAnalyticsResponse {
  period_days: number;
  total_tool_calls: number;
  unique_tools: number;
  blocked_duplicates: number;
  efficiency_percent: number;
  top_tools: Array<{ name: string; count: number; avg_duration_ms: number }>;
  calls_by_day: Array<{ date: string; count: number }>;
}

export interface ContextUtilizationResponse {
  session_id: string;
  current_turns: number;
  max_turns: number;
  estimated_context_tokens: number;
  max_context_tokens: number;
  utilization_percent: number;
  entities_tracked: string[];
}

export interface PerformanceMetricsResponse {
  period_days: number;
  avg_response_time_ms: number;
  p95_response_time_ms: number;
  by_model: Array<{ model: string; avg_ms: number; p95_ms: number; request_count: number }>;
  daily_trend: Array<{ date: string; avg_ms: number; request_count: number }>;
}

export interface ScheduledQueriesSummaryResponse {
  total_schedules: number;
  active_schedules: number;
  total_runs_this_week: number;
  success_count: number;
  failure_count: number;
  total_runs_all_time: number;
}

export interface RecipeSummaryItem {
  recipe_name: string;
  hit_count: number;
  avg_duration_ms: number | null;
  last_fired: string | null;
}

// STORY-301: GET /api/analytics/compound/summary (admin only). Every label
// in it comes from a closed set on the server; no query or part text.
export interface CompoundLatencyBucket {
  p50_ms: number | null;
  p95_ms: number | null;
  count: number;
  extra_p50_ms: number | null;
  extra_p95_ms: number | null;
}

export interface CompoundRecentFailure {
  created_at: string | null;
  status: 'fail' | 'skipped';
  compound_class: string | null;
  dispatch_plan: string;
  part_id: string;
  domain: string;
  path: string;
  fail_reason: string;
  duration_ms: number | null;
}

export interface CompoundSummary {
  period_days: number;
  chat_response_count: number;
  class_distribution: { total: number; counts: Record<string, number>; shares: Record<string, number> };
  classifier_coverage: {
    total: number;
    counts: Record<string, number>;
    shares: Record<string, number>;
    reasons: Record<string, number>;
  };
  plan_distribution: { total: number; counts: Record<string, number> };
  // STORY-297: who planned an executed sequential split (absent on older servers)
  plan_sources?: { total: number; counts: Record<string, number> };
  steal_proxy: { count: number; parallel_turns: number; rate: number | null };
  part_failure: {
    fail_count: number;
    part_count: number;
    split_turns: number;
    rate: number | null;
    skipped_count: number;
    by_reason: Record<string, number>;
    by_position: Record<string, number>;
    by_domain: Record<string, number>;
  };
  recent_failures: CompoundRecentFailure[];
  extra_latency: {
    baseline: { key: string; p50_ms: number | null; p95_ms: number | null; count: number };
    by_compound_class: Record<string, CompoundLatencyBucket>;
    by_dispatch_plan: Record<string, CompoundLatencyBucket>;
  };
  shadow_disagreement: { count: number; shadow_turns: number; rate: number | null };
  wrong_split: {
    sequential_as_parallel: number;
    parallel_as_sequential: number;
    simple_as_split: number;
    count: number;
  };
}

export interface ProviderVolumeResponse {
  labels: string[];
  series: Record<string, number[]>;
  total_by_provider: Record<string, number>;
}

// HITL write-confirmation (STORY-165)
export interface HITLPendingItem {
  confirmation_id: string;
  tool_name: string;
  preview: string;
  expires_in_seconds: number;
  can_approve: boolean;
}

export interface HITLPendingResponse {
  pending: HITLPendingItem[];
  count: number;
}

export interface HITLApproveResponse {
  status: string;
  confirmation_id: string;
  tool_name: string;
  result?: string | null;
  error?: string | null;
}

export interface HITLDenyResponse {
  status: string;
  confirmation_id: string;
  denied: boolean;
}

/**
 * Error thrown for non-OK API responses. Carries the HTTP status so callers
 * can distinguish auth failures (401) from transient errors (network, 5xx).
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export class ApiClient {
  private baseUrl: string;
  private authToken: string | null = null;
  private onUnauthorized: (() => void) | null = null;

  /** Public base URL for SSO redirects and similar direct-access needs. */
  get apiBaseUrl(): string { return this.baseUrl; }

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set or clear the JWT auth token.
   * When set, all subsequent requests include Authorization: Bearer <token>.
   */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  getAuthToken(): string | null {
    return this.authToken;
  }

  /**
   * Register a callback that fires on 401 responses (e.g. auto-logout).
   */
  setOnUnauthorized(cb: (() => void) | null): void {
    this.onUnauthorized = cb;
  }

  private generateRequestId(): string {
    return generateUUID();
  }

  /**
   * Build default headers including auth and language when available.
   */
  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Request-Id': this.generateRequestId(),
      ...extra,
    };
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    // Send the UI language so the backend can localise response messages.
    // Read from the persisted app store (same key appStore uses).
    try {
      const raw = localStorage.getItem('mcp-chat-storage-v3');
      if (raw) {
        const parsed = JSON.parse(raw);
        const lang = parsed?.state?.settings?.language;
        if (lang) {
          headers['Accept-Language'] = lang;
        }
      }
    } catch {
      // ignore — header will be absent, backend defaults to 'ru'
    }
    return headers;
  }

  /**
   * Central response handler — throws on error, handles 401.
   * @param skipUnauthorizedHook - If true, don't fire onUnauthorized (used for login/register
   *   endpoints where 401 means "wrong credentials", not "session expired").
   */
  private async handleResponse<T>(response: Response, skipUnauthorizedHook = false): Promise<T> {
    if (response.status === 401) {
      // Only treat 401 as session expiry when a token is actually wired.
      // A 401 with no token means the request went out before auth
      // hydration (or from an anonymous view) — not an expired session
      // (STORY-197: boot-time 401s must not log the user out).
      if (!skipUnauthorizedHook && this.authToken) {
        this.onUnauthorized?.();
      }
      const data = await response.json().catch(() => ({}));
      throw new ApiError(this.extractErrorMessage(data, 'Unauthorized'), 401);
    }
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(this.extractErrorMessage(data, `HTTP error! status: ${response.status}`), response.status);
    }
    return response.json();
  }

  /**
   * Extract a human-readable error message from API error responses.
   * Handles both simple {detail: string} and Pydantic validation {detail: [{msg, loc}]} formats.
   */
  private extractErrorMessage(data: any, fallback: string): string {
    // Some backend bodies carry only {error} (collect-more 400/502, the
    // paginate 400/404): that text is the explanation — show it rather than
    // "HTTP error! status: NNN" (BUG-1.5.0_3).
    if (!data?.detail) {
      return typeof data?.error === 'string' && data.error ? data.error : fallback;
    }

    // Simple string detail
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    
    // Pydantic validation error: array of {loc, msg, type}
    if (Array.isArray(data.detail)) {
      const messages = data.detail
        .map((err: any) => {
          const field = err.loc?.slice(1).join('.') || 'field';
          return `${field}: ${err.msg}`;
        })
        .join('; ');
      return messages || fallback;
    }
    
    // Object detail with a machine reason (STORY-227 override validation:
    // {detail: {reason: "steals:...", ...}}) — surface the reason.
    if (typeof data.detail === 'object' && typeof data.detail.reason === 'string') {
      return data.detail.reason;
    }

    // Unknown format
    return fallback;
  }

  // =================== Auth API Methods ===================

  async login(email: string, password: string): Promise<TokenResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ email, password }),
    });
    // Skip onUnauthorized hook — 401 here means "wrong credentials", not session expiry
    return this.handleResponse<TokenResponse>(response, true);
  }

  async register(username: string, email: string, password: string): Promise<RegisterResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/register`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ username, email, password }),
    });
    // Skip onUnauthorized hook — 401 here is not a session issue
    return this.handleResponse<RegisterResponse>(response, true);
  }

  async verifyEmail(token: string): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/verify-email`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token }),
    });
    return this.handleResponse<MessageResponse>(response);
  }

  async resendVerification(email: string): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/resend-verification`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ email }),
    });
    return this.handleResponse<MessageResponse>(response);
  }

  async forgotPassword(email: string): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/forgot-password`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ email }),
    });
    return this.handleResponse<MessageResponse>(response);
  }

  async resetPassword(token: string, newPassword: string): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/reset-password`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    return this.handleResponse<MessageResponse>(response);
  }

  async getProfile(): Promise<UserProfile> {
    const response = await fetch(`${this.baseUrl}/api/users/me`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<UserProfile>(response);
  }

  async updateThresholds(thresholds: ThresholdUpdate[]): Promise<ThresholdResponse[]> {
    const response = await fetch(`${this.baseUrl}/api/users/me/thresholds`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(thresholds),
    });
    return this.handleResponse<ThresholdResponse[]>(response);
  }

  // =================== Existing API Methods (updated with auth headers) ===================

  /**
   * POST /chat. `signal` (STORY-299) lets the chat that sent it stop waiting:
   * Stop, closing the chat and logout abort it. Aborting cancels only the
   * browser's wait; the server work stops through cancelTurn (STORY-511).
   */
  async chat(request: ChatRequest, signal?: AbortSignal): Promise<ChatResponse> {
    const response = await fetch(`${this.baseUrl}/chat`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(request),
      signal,
    });
    return this.handleResponse<ChatResponse>(response);
  }

  /** STORY-524: the state of one of the caller's turns (404 once unknown). */
  async getTurn(turnId: string): Promise<ChatTurnState> {
    const response = await fetch(`${this.baseUrl}/api/chat/turns/${encodeURIComponent(turnId)}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ChatTurnState>(response);
  }

  /** STORY-511: stop the server work of a turn (Stop, closing the chat). */
  async cancelTurn(turnId: string, reason: 'stop' | 'close_chat'): Promise<{ turn_id: string; status: TurnCancelStatus }> {
    const response = await fetch(`${this.baseUrl}/api/chat/turns/${encodeURIComponent(turnId)}/cancel`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ reason }),
    });
    return this.handleResponse(response);
  }

  /**
   * STORY-511: logout. Sent while the token is still set, with keepalive so
   * the request survives the page tearing down right after. A 401 here must
   * not re-enter the logout it is part of.
   */
  async cancelTurns(turnIds: string[]): Promise<{ turns: Record<string, TurnCancelStatus> }> {
    const response = await fetch(`${this.baseUrl}/api/chat/turns/cancel`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ turn_ids: turnIds }),
      keepalive: true,
    });
    return this.handleResponse(response, true);
  }

  async getMCPServers(refresh: boolean = false): Promise<MCPServerInfo[]> {
    const url = `${this.baseUrl}/mcp/servers${refresh ? '?refresh=1' : ''}`;
    const response = await fetch(url, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<MCPServerInfo[]>(response);
  }

  async callTool(server: string, tool: string, parameters: Record<string, any>): Promise<any> {
    const response = await fetch(`${this.baseUrl}/mcp/call-tool`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ server, tool, parameters }),
    });
    return this.handleResponse<any>(response);
  }

  async healthCheck(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/health`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<any>(response);
  }

  async getVersion(): Promise<{
    version: string;
    build_date?: string;
    git_hash?: string;
    cert_expires?: string;
    cert_days?: number;
    cert_calls?: number;
  }> {
    const response = await fetch(`${this.baseUrl}/api/version`);
    return this.handleResponse(response);
  }

  // =================== Cluster Mode Preferences ===================

  async getClusterMode(): Promise<{
    preference: { mode: string; synthesis_mode: string; selected_cluster_ids: string[] };
    multi_cluster_available: boolean;
    cluster_count: number;
    is_cluster_bound: boolean;
    clusters: Array<{ id: string; name: string; base_url: string }>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/users/me/cluster-mode`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async setClusterMode(body: {
    mode?: string;
    synthesis_mode?: string;
    selected_cluster_ids?: string[];
  }): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/api/users/me/cluster-mode`, {
      method: 'PATCH',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse(response);
  }

  /**
   * Clear server-side conversation history for a session.
   * This should be called when starting a new chat to ensure
   * the LLM doesn't have stale context from previous conversations.
   */
  async clearConversationHistory(sessionId: string): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/chat/history/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ status: string; message: string }>(response);
  }

  /**
   * Get conversation statistics for a session.
   * Useful for debugging or displaying context info to users.
   */
  async getConversationStats(sessionId: string): Promise<ConversationStats> {
    const response = await fetch(`${this.baseUrl}/chat/history/${encodeURIComponent(sessionId)}/stats`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ConversationStats>(response);
  }

  /**
   * Add and initialize a new LLM provider in the backend.
   */
  async addLLMProvider(providerConfig: {
    type: string;
    name?: string;
    model: string;
    endpoint?: string;
    apiKey?: string;
    credentials?: string;
    scope?: string;
    /** Registers the provider disabled (usable only after /enable) when false. Defaults to true. */
    enabled?: boolean;
    settings?: {
      maxTokens?: number;
      temperature?: number;
      topP?: number;
      frequencyPenalty?: number;
      presencePenalty?: number;
    };
  }): Promise<{ status: string; message: string; provider_name?: string; saved_to_storage?: boolean; available_providers?: any[] }> {
    const response = await fetch(`${this.baseUrl}/api/llm/providers/add`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(providerConfig),
    });
    return this.handleResponse<{ status: string; message: string; provider_name?: string; available_providers?: any[] }>(response);
  }

  /**
   * Test if a provider configuration works without adding it.
   */
  async testLLMProvider(providerConfig: {
    type: string;
    name?: string;
    model: string;
    endpoint?: string;
    apiKey?: string;
    credentials?: string;
    scope?: string;
    settings?: {
      maxTokens?: number;
      temperature?: number;
    };
  }): Promise<{ status: string; message: string; available?: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/llm/providers/test`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(providerConfig),
    });
    return this.handleResponse<{ status: string; message: string; available?: boolean }>(response);
  }

  /**
   * Get list of all available (initialized) LLM providers.
   */
  async getAvailableProviders(): Promise<{ status: string; providers: any[]; count: number }> {
    const response = await fetch(`${this.baseUrl}/api/llm/providers/available`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ status: string; providers: any[]; count: number }>(response);
  }

  /** Get current backend config for a provider type.
   *  Non-secret fields returned in full; secrets return {is_set: boolean}. */
  async getProviderConfig(providerType: string): Promise<{
    status: string;
    provider_type: string;
    config: Record<string, any>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/llm/providers/${providerType}/config`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Deactivate an LLM provider and remove its configuration.
   * Removes from memory, storage (Vault/.env), and MCP sync.
   * When a model is given, only that model's registration is deactivated —
   * sibling models of the same provider stay active (STORY-219).
   * @param providerType Provider type to deactivate ('ollama', 'gigachat', etc.)
   * @param model Optional model name to deactivate just one model
   */
  async deactivateLLMProvider(providerType: string, model?: string): Promise<{
    status: string;
    message: string;
    removed_providers: string[];
    storage_cleared: boolean;
    mcp_synced: boolean;
    available_providers: any[];
  }> {
    const query = model ? `?model=${encodeURIComponent(model)}` : '';
    const response = await fetch(`${this.baseUrl}/api/llm/providers/${providerType}/deactivate${query}`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
    });
    return this.handleResponse(response);
  }

  /**
   * Turn on an already-registered provider (or one of its models). Pure
   * runtime toggle — does not touch Vault/.env. Returns status 'not_found'
   * if nothing matches (e.g. it was never registered via addLLMProvider).
   */
  async enableLLMProvider(providerType: string, model?: string): Promise<{
    status: string;
    message: string;
    provider_names?: string[];
    mcp_synced?: boolean;
    available_providers?: any[];
  }> {
    const query = model ? `?model=${encodeURIComponent(model)}` : '';
    const response = await fetch(`${this.baseUrl}/api/llm/providers/${providerType}/enable${query}`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
    });
    return this.handleResponse(response);
  }

  /**
   * Turn off an already-registered provider (or one of its models) without
   * deleting its stored credentials/config. Pure runtime toggle.
   */
  async disableLLMProvider(providerType: string, model?: string): Promise<{
    status: string;
    message: string;
    provider_names?: string[];
    mcp_synced?: boolean;
    available_providers?: any[];
  }> {
    const query = model ? `?model=${encodeURIComponent(model)}` : '';
    const response = await fetch(`${this.baseUrl}/api/llm/providers/${providerType}/disable${query}`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
    });
    return this.handleResponse(response);
  }

  // =================== Configuration Status ===================

  /**
   * Get system configuration status (no auth required — setup flow).
   * Returns per-credential boolean and overall configured flag.
   */
  async getConfigStatus(): Promise<ConfigStatus> {
    const response = await fetch(`${this.baseUrl}/api/config/status`);
    return this.handleResponse<ConfigStatus>(response);
  }

  /** Check if SSO is enabled (no auth required). */
  async getSsoConfig(): Promise<{ enabled: boolean; issuer_url: string | null }> {
    const response = await fetch(`${this.baseUrl}/api/auth/sso/config`);
    return this.handleResponse(response);
  }

  /** Trade the one-time SSO login code for the session token (STORY-349). */
  async exchangeSsoCode(code: string): Promise<{ access_token: string }> {
    const response = await fetch(`${this.baseUrl}/api/auth/sso/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    return this.handleResponse(response, true);
  }

  // =================== Vault API Methods ===================

  /**
   * Get Vault connection status.
   */
  async getSecretsStatus(): Promise<VaultStatus> {
    const response = await fetch(`${this.baseUrl}/api/secrets/status`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<VaultStatus>(response);
  }

  /**
   * List all configured secrets (values masked for security).
   */
  async listSecrets(): Promise<SecretsListResponse> {
    const response = await fetch(`${this.baseUrl}/api/secrets/list`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<SecretsListResponse>(response);
  }

  /**
   * Test a secret/credential before saving.
   */
  async testSecret(secretType: string, secretValue: string): Promise<{ valid: boolean; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/secrets/test`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ secret_type: secretType, secret_value: secretValue }),
    });
    return this.handleResponse(response);
  }

  /**
   * Set a secret in Vault and trigger hot-reload of affected services.
   */
  async setSecret(path: string, value: string): Promise<SetSecretResponse> {
    const response = await fetch(`${this.baseUrl}/api/secrets/set`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ path, value }),
    });
    return this.handleResponse<SetSecretResponse>(response);
  }

  /**
   * Fetch a specific page of a paginated result set.
   * Bypasses the LLM — fetches directly from the cluster API.
   */
  async fetchPage(paginationId: string, page: number): Promise<{
    items: any[];
    pagination: PaginationMeta & { items_on_page: number; has_next?: boolean };
  }> {
    const response = await fetch(
      `${this.baseUrl}/api/paginate/${encodeURIComponent(paginationId)}?page=${page}`,
      { headers: this.buildHeaders() },
    );
    return this.handleResponse(response);
  }

  /**
   * Collect more data from the API for a paginated result set.
   * Fetches the next batch using the stored continuation key.
   */
  async collectMore(paginationId: string): Promise<CollectMoreResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/paginate/${encodeURIComponent(paginationId)}/collect-more`,
      {
        method: 'POST',
        headers: this.buildHeaders(),
      },
    );
    return this.handleResponse(response);
  }

  // =================== Shared Settings ===================

  async shareSettings(settingKeys: string[], targetUserIds: number[]): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/settings/share`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ setting_keys: settingKeys, target_user_ids: targetUserIds }),
    });
    return this.handleResponse(response);
  }

  async getSharedSettings(): Promise<{ items: any[]; pending_count: number }> {
    const response = await fetch(`${this.baseUrl}/api/settings/shared`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async acceptSharedSettings(shareId: number, merge: boolean = true): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/settings/shared/${shareId}/accept`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ merge }),
    });
    return this.handleResponse(response);
  }

  async rejectSharedSettings(shareId: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/settings/shared/${shareId}/reject`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Reload backend configuration from Vault.
   */
  async reloadConfig(): Promise<ReloadResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/reload-config`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ReloadResponse>(response);
  }

  /**
   * Reload all services (MCP server and chat-backend).
   */
  async reloadAllServices(): Promise<ReloadResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/reload-all`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ReloadResponse>(response);
  }

  // =================== .env Secrets Management ===================

  /**
   * Check which secrets are still in .env vs managed by Vault.
   */
  async getEnvSecretsStatus(): Promise<{ secrets: Array<{ key: string; in_env: boolean; in_vault: boolean }>; all_in_vault: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/admin/env-secrets-status`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Clear secret values from .env file (admin only). Requires all secrets to be in Vault first.
   */
  async clearEnvSecrets(): Promise<ClearEnvSecretsResult> {
    const response = await fetch(`${this.baseUrl}/api/admin/clear-env-secrets`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Migrate a single secret from .env to Vault (admin only).
   * @param key The environment variable name to migrate (e.g., 'GIGACHAT_CREDENTIALS')
   */
  async migrateSecretToVault(key: string): Promise<{ status: string; key: string; vault_path: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/admin/migrate-secret-to-vault`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ key }),
    });
    return this.handleResponse(response);
  }

  // =================== Certificate API Methods ===================

  /**
   * Get certificate status including expiry and validity.
   */
  async getCertificateStatus(): Promise<CertificateStatus> {
    const response = await fetch(`${this.baseUrl}/api/certificates/status`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<CertificateStatus>(response);
  }

  /**
   * Upload a new certificate.pem file.
   * @param file The certificate file to upload
   * @param storeInVault If true, store as base64 in Vault KV. If false, write to filesystem.
   */
  async uploadCertificate(file: File, storeInVault: boolean = false): Promise<CertificateUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    // Build headers without Content-Type (FormData sets it automatically with boundary)
    const headers = this.buildHeaders();
    
    const url = `${this.baseUrl}/api/certificates/upload?store_in_vault=${storeInVault}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });
    return this.handleResponse<CertificateUploadResponse>(response);
  }

  // =================== Extension Certificate API Methods ===================

  /**
   * List all extension certificates.
   */
  async listExtensionCertificates(): Promise<ExtensionCertListResponse> {
    const response = await fetch(`${this.baseUrl}/api/certificates/extensions`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ExtensionCertListResponse>(response);
  }

  /**
   * Upload an extension certificate (.pem file).
   */
  async uploadExtensionCertificate(file: File): Promise<ExtensionCertUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const headers = this.buildHeaders();

    const response = await fetch(`${this.baseUrl}/api/certificates/extensions/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    return this.handleResponse<ExtensionCertUploadResponse>(response);
  }

  /**
   * Revoke an extension certificate.
   */
  async revokeExtensionCertificate(certId: string): Promise<ExtensionCertRevokeResponse> {
    const response = await fetch(`${this.baseUrl}/api/certificates/extensions/${certId}/revoke`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ExtensionCertRevokeResponse>(response);
  }

  /**
   * Get details of a specific extension certificate.
   */
  async getExtensionCertificate(certId: string): Promise<ExtensionCertificate> {
    const response = await fetch(`${this.baseUrl}/api/certificates/extensions/${certId}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ExtensionCertificate>(response);
  }

  // =================== Authenticated binary fetch (STORY-184) ===================

  /** Fetch an auth-guarded backend path (e.g. dashboard screenshots) and
   *  return an object URL for <img src>. Caller revokes it when done. */
  async fetchAuthedObjectUrl(path: string): Promise<string> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      headers: this.buildHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // =================== Analytics scope (STORY-188) ===================

  /** Admin-only global view flag. When true, analytics/stats requests carry
   *  all_users=true (the backend rejects it with 403 for non-admins). */
  private analyticsAllUsers = false;

  setAnalyticsScope(allUsers: boolean): void {
    this.analyticsAllUsers = allUsers;
  }

  /** Append all_users=true to a URL when the global scope is active. */
  private withScope(url: string): string {
    if (!this.analyticsAllUsers) return url;
    return url + (url.includes('?') ? '&' : '?') + 'all_users=true';
  }

  // =================== Token Statistics API Methods ===================

  /**
   * Get token usage statistics for a time range.
   */
  async getTokenStats(params: TokenStatsParams = {}): Promise<TokenStatsResponse> {
    const searchParams = new URLSearchParams();
    if (params.start) searchParams.append('start', params.start);
    if (params.end) searchParams.append('end', params.end);
    if (params.provider) searchParams.append('provider', params.provider);
    if (params.model) searchParams.append('model', params.model);
    const queryString = searchParams.toString();
    const url = this.withScope(`${this.baseUrl}/api/stats/tokens${queryString ? `?${queryString}` : ''}`);
    const response = await fetch(url, { headers: this.buildHeaders() });
    return this.handleResponse<TokenStatsResponse>(response);
  }

  /**
   * Get quick summary for common time periods (today, week, month, all-time).
   */
  async getTokenSummary(): Promise<QuickSummary> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/stats/tokens/summary`), {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<QuickSummary>(response);
  }

  /**
   * Get token usage breakdown by provider.
   */
  async getUsageByProvider(start?: string, end?: string): Promise<ProviderUsage[]> {
    const searchParams = new URLSearchParams();
    if (start) searchParams.append('start', start);
    if (end) searchParams.append('end', end);
    const queryString = searchParams.toString();
    const url = this.withScope(`${this.baseUrl}/api/stats/tokens/by-provider${queryString ? `?${queryString}` : ''}`);
    const response = await fetch(url, { headers: this.buildHeaders() });
    return this.handleResponse<ProviderUsage[]>(response);
  }

  /**
   * Get token usage breakdown by model.
   */
  async getUsageByModel(start?: string, end?: string): Promise<ModelUsage[]> {
    const searchParams = new URLSearchParams();
    if (start) searchParams.append('start', start);
    if (end) searchParams.append('end', end);
    const queryString = searchParams.toString();
    const url = this.withScope(`${this.baseUrl}/api/stats/tokens/by-model${queryString ? `?${queryString}` : ''}`);
    const response = await fetch(url, { headers: this.buildHeaders() });
    return this.handleResponse<ModelUsage[]>(response);
  }

  /**
   * Get recent token usage records (for debugging).
   */
  async getRecentTokenRecords(limit: number = 50): Promise<any[]> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/stats/tokens/recent?limit=${limit}`), {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<any[]>(response);
  }

  // =================== Scheduled Queries API Methods ===================

  async listScenarios(language: 'ru' | 'en' = 'ru'): Promise<ScenarioSummary[]> {
    const response = await fetch(`${this.baseUrl}/api/scenarios?language=${language}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ScenarioSummary[]>(response);
  }

  async createScheduledQuery(body: ScheduledQueryCreateBody): Promise<ScheduledQueryResponse> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<ScheduledQueryResponse>(response);
  }

  async listScheduledQueries(): Promise<ScheduledQueryResponse[]> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ScheduledQueryResponse[]>(response);
  }

  async getScheduledQuery(id: number): Promise<ScheduledQueryResponse> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries/${id}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ScheduledQueryResponse>(response);
  }

  async updateScheduledQuery(id: number, body: ScheduledQueryUpdateBody): Promise<ScheduledQueryResponse> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries/${id}`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<ScheduledQueryResponse>(response);
  }

  async deleteScheduledQuery(id: number): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries/${id}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ status: string; message: string }>(response);
  }

  async runScheduledQueryNow(id: number): Promise<ScheduledQueryResultResponse> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries/${id}/run`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ScheduledQueryResultResponse>(response);
  }

  async getScheduledQueryHistory(id: number, limit: number = 50): Promise<ScheduledQueryResultResponse[]> {
    const response = await fetch(`${this.baseUrl}/api/scheduled-queries/${id}/history?limit=${limit}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ScheduledQueryResultResponse[]>(response);
  }

  // =================== Telegram API Methods ===================

  async getTelegramStatus(): Promise<TelegramLinkStatusResponse> {
    const response = await fetch(`${this.baseUrl}/api/settings/telegram`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<TelegramLinkStatusResponse>(response);
  }

  async generateTelegramToken(): Promise<TelegramTokenGenerateResponse> {
    const response = await fetch(`${this.baseUrl}/api/settings/telegram/generate-token`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<TelegramTokenGenerateResponse>(response);
  }

  async unlinkTelegram(): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/settings/telegram/unlink`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ status: string; message: string }>(response);
  }

  /** STORY-304: send an incident card to the caller's own linked Telegram
   *  chat. The server redacts the text again and sends nothing if it cannot. */
  async sendCardToTelegram(text: string, problemId?: string): Promise<SendCardResponse> {
    const response = await fetch(`${this.baseUrl}/api/telegram/send-card`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ text, problem_id: problemId ?? null }),
    });
    return this.handleResponse<SendCardResponse>(response);
  }

  async getTelegramBotInfo(): Promise<TelegramBotInfoResponse> {
    const response = await fetch(`${this.baseUrl}/api/settings/telegram/bot-info`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<TelegramBotInfoResponse>(response);
  }

  // =================== User Settings API Methods ===================

  /**
   * Get the user's server-side settings JSON.
   * Returns empty {} if none saved yet — frontend merges with defaults.
   */
  async getSettings(): Promise<{ settings: Record<string, any> }> {
    const response = await fetch(`${this.baseUrl}/api/settings`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ settings: Record<string, any> }>(response);
  }

  /**
   * Upsert the full settings JSON blob for the current user.
   */
  async saveSettings(settings: Record<string, any>): Promise<{ status: string }> {
    const response = await fetch(`${this.baseUrl}/api/settings`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ settings }),
    });
    return this.handleResponse<{ status: string }>(response);
  }

  // =================== Chat Sessions API Methods ===================

  /**
   * List all chat sessions for the current user (metadata only, no messages).
   */
  async listSessions(): Promise<Array<{
    id: string;
    title: string;
    pinned: boolean;
    created_at: string;
    updated_at: string;
    message_count: number;
  }>> {
    const response = await fetch(`${this.baseUrl}/api/sessions`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Create a new chat session on the server.
   */
  async createSession(id?: string, title?: string, signal?: AbortSignal): Promise<{
    id: string;
    title: string;
    pinned: boolean;
    created_at: string;
    updated_at: string;
    messages: any[];
  }> {
    const response = await fetch(`${this.baseUrl}/api/sessions`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ id, title: title || 'Новый чат' }),
      signal,
    });
    return this.handleResponse(response);
  }

  /**
   * Get a session with all its messages.
   */
  async getSession(sessionId: string): Promise<{
    id: string;
    title: string;
    pinned: boolean;
    created_at: string;
    updated_at: string;
    messages: Array<{
      id: string;
      type: string;
      content: string;
      metadata: any;
      created_at: string;
    }>;
    /** STORY-524: the session's turn still being answered on the server. */
    pending_turn?: ChatTurnState | null;
  }> {
    const response = await fetch(`${this.baseUrl}/api/sessions/${encodeURIComponent(sessionId)}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Update session title and/or pinned state.
   */
  async updateSession(sessionId: string, updates: { title?: string; pinned?: boolean }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  /**
   * Delete a chat session and all its messages.
   */
  async deleteServerSession(sessionId: string): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/sessions/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Add a message to a session (fire-and-forget from the UI).
   */
  async addMessage(sessionId: string, message: {
    id: string;
    type: string;
    content: string;
    metadata?: any;
    created_at?: string;
  }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(message),
    });
    return this.handleResponse(response);
  }

  /**
   * Clear all messages in a session.
   */
  async clearSessionMessages(sessionId: string): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  // =================== Admin User Management API Methods ===================

  async adminListUsers(params: {
    page?: number;
    page_size?: number;
    search?: string;
    active_only?: boolean;
  } = {}): Promise<AdminUserListResponse> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', String(params.page));
    if (params.page_size) searchParams.append('page_size', String(params.page_size));
    if (params.search) searchParams.append('search', params.search);
    if (params.active_only) searchParams.append('active_only', 'true');
    const queryString = searchParams.toString();
    const url = `${this.baseUrl}/api/admin/users${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(url, { headers: this.buildHeaders() });
    return this.handleResponse<AdminUserListResponse>(response);
  }

  async adminDeactivateUser(userId: number): Promise<AdminActionResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/${userId}/deactivate`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AdminActionResponse>(response);
  }

  async adminActivateUser(userId: number): Promise<AdminActionResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/${userId}/activate`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AdminActionResponse>(response);
  }

  async adminChangeRole(userId: number, role: string): Promise<{ message: string; user_id: number; role: string }> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/${userId}/role`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ role }),
    });
    return this.handleResponse<{ message: string; user_id: number; role: string }>(response);
  }

  async adminVerifyEmail(userId: number): Promise<{ message: string; user_id: number; email_verified: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/${userId}/verify-email`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ message: string; user_id: number; email_verified: boolean }>(response);
  }

  async adminCreateUser(body: AdminCreateUserRequest): Promise<AdminUserView> {
    const response = await fetch(`${this.baseUrl}/api/admin/users`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<AdminUserView>(response);
  }

  async adminSetBudget(userId: number, tokenBudgetMonthly: number | null): Promise<AdminActionResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/${userId}/budget`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ token_budget_monthly: tokenBudgetMonthly }),
    });
    return this.handleResponse<AdminActionResponse>(response);
  }

  async adminResetPassword(userId: number, newPassword: string): Promise<AdminActionResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/${userId}/password`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ new_password: newPassword }),
    });
    return this.handleResponse<AdminActionResponse>(response);
  }

  async adminGetUsersUsage(): Promise<AdminUserUsageResponse> {
    const response = await fetch(`${this.baseUrl}/api/admin/users/usage`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AdminUserUsageResponse>(response);
  }

  // =================== Dispatch Overrides API (STORY-227/228) ===================

  async adminListOverrides(params: { scope?: string; target?: string } = {}): Promise<DispatchOverrideView[]> {
    const sp = new URLSearchParams();
    if (params.scope) sp.append('scope', params.scope);
    if (params.target) sp.append('target', params.target);
    const qs = sp.toString();
    const url = `${this.baseUrl}/api/admin/overrides${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { headers: this.buildHeaders() });
    return this.handleResponse<DispatchOverrideView[]>(response);
  }

  async adminCreateOverride(body: DispatchOverrideCreate): Promise<DispatchOverrideView> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<DispatchOverrideView>(response);
  }

  async adminDeleteOverride(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides/${id}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    if (!response.ok && response.status !== 204) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(this.extractErrorMessage(data, `HTTP error! status: ${response.status}`), response.status);
    }
  }

  async adminDryRunDispatch(body: {
    query: string;
    language?: string;
    candidate?: DispatchOverrideCreate | null;
  }): Promise<DispatchDryRunResult> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides/dry-run`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<DispatchDryRunResult>(response);
  }

  async adminGetOverrideTargets(): Promise<DispatchOverrideTargets> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides/targets`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<DispatchOverrideTargets>(response);
  }

  async adminReloadToolDescriptions(): Promise<{
    status: string;
    overrides_loaded?: number;
    tools_registered?: number;
    cache_refreshed?: boolean;
  }> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides/tool-descriptions/reload`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async adminGetToolDescriptions(): Promise<{
    status: string;
    path: string;
    version: number;
    overrides: Record<string, any>;
    tools: string[];
  }> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides/tool-descriptions`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async adminPutToolDescriptions(overrides: Record<string, any>): Promise<{
    status: string;
    path?: string;
    written?: number;
    cache_refreshed?: boolean;
    // BUG-1.3.7_3: override keys that match no currently-loaded tool (typo guard).
    unknown_tools?: string[];
    warning?: string;
  }> {
    const response = await fetch(`${this.baseUrl}/api/admin/overrides/tool-descriptions`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ overrides }),
    });
    return this.handleResponse(response);
  }

  async getRegistrationStatus(): Promise<{ self_registration_enabled: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/auth/registration-status`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ self_registration_enabled: boolean }>(response);
  }

  // =================== Analytics API Methods ===================

  /**
   * Get session cost analytics including token counts and cost in RUB.
   */
  async getSessionCost(sessionId: string): Promise<SessionCostResponse> {
    const response = await fetch(`${this.baseUrl}/api/analytics/session/${encodeURIComponent(sessionId)}/cost`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<SessionCostResponse>(response);
  }

  /**
   * Get tool call analytics summary for a given period.
   */
  async getToolAnalytics(days: number = 30): Promise<ToolAnalyticsResponse> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/analytics/tools/summary?days=${days}`), {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ToolAnalyticsResponse>(response);
  }

  /**
   * Get context window utilization for a session.
   */
  async getContextUtilization(sessionId: string): Promise<ContextUtilizationResponse> {
    const response = await fetch(`${this.baseUrl}/api/analytics/session/${encodeURIComponent(sessionId)}/context`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ContextUtilizationResponse>(response);
  }

  /**
   * Get performance metrics (response times, latency) for a given period.
   */
  async getPerformanceMetrics(days: number = 7): Promise<PerformanceMetricsResponse> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/analytics/performance?days=${days}`), {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<PerformanceMetricsResponse>(response);
  }

  /**
   * Get scheduled queries execution summary.
   */
  async getScheduledQueriesSummary(): Promise<ScheduledQueriesSummaryResponse> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/analytics/scheduled-queries/summary`), {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<ScheduledQueriesSummaryResponse>(response);
  }

  /**
   * Get recipe hit rate summary for the last N days.
   */
  async getRecipeSummary(days: number = 30): Promise<RecipeSummaryItem[]> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/analytics/recipes/summary?days=${days}`), {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<RecipeSummaryItem[]>(response);
  }

  /**
   * Compound-query statistics for the last N days (STORY-301, admin only).
   * Deliberately not withScope: the data is global and the route is
   * admin-gated, so there is no per-user view to scope to.
   */
  async getCompoundSummary(days: number = 7): Promise<CompoundSummary> {
    const response = await fetch(`${this.baseUrl}/api/analytics/compound/summary?days=${days}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<CompoundSummary>(response);
  }

  /**
   * Get query volume broken down by LLM provider over time.
   */
  async getVolumeByProvider(days: number = 7, signal?: AbortSignal): Promise<ProviderVolumeResponse> {
    const response = await fetch(this.withScope(`${this.baseUrl}/api/analytics/volume?days=${days}&granularity=day`), {
      headers: this.buildHeaders(),
      signal,
    });
    return this.handleResponse<ProviderVolumeResponse>(response);
  }

  // =================== Knowledge Base API (STORY-303) ===================

  async listKbDocuments(): Promise<KbDocument[]> {
    const response = await fetch(`${this.baseUrl}/api/kb/documents`, {
      headers: this.buildHeaders(),
    });
    const data = await this.handleResponse<{ documents: KbDocument[] }>(response);
    return data.documents || [];
  }

  /** Throws KbIngestBlockedError (finding kinds only) when a secret is found. */
  async ingestKbDocument(body: KbIngestRequest): Promise<KbIngestResponse> {
    const response = await fetch(`${this.baseUrl}/api/kb/documents`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    if (response.status === 422) {
      const data = await response.clone().json().catch(() => ({}));
      if (data?.detail?.error === 'ingest_blocked') {
        throw new KbIngestBlockedError(Array.isArray(data.detail.findings) ? data.detail.findings : []);
      }
    }
    return this.handleResponse<KbIngestResponse>(response);
  }

  async deleteKbDocument(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/kb/documents/${id}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    await this.handleResponse<unknown>(response);
  }

  async searchKb(q: string, topK = 5, cluster?: string): Promise<KbSearchHit[]> {
    const sp = new URLSearchParams({ q, top_k: String(topK) });
    if (cluster) sp.append('cluster', cluster);
    const response = await fetch(`${this.baseUrl}/api/kb/search?${sp.toString()}`, {
      headers: this.buildHeaders(),
    });
    const data = await this.handleResponse<{ results: KbSearchHit[] }>(response);
    return data.results || [];
  }

  // =================== Cluster Management API ===================

  async listClusters(activeOnly = false): Promise<any> {
    const params = activeOnly ? '?active_only=true' : '';
    const response = await fetch(`${this.baseUrl}/api/clusters${params}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async getCluster(id: number, unmask = false): Promise<any> {
    const params = unmask ? '?unmask=true' : '';
    const response = await fetch(`${this.baseUrl}/api/clusters/${id}${params}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async addCluster(cluster: {
    name: string;
    base_url: string;
    cluster_api_token: string;
    environment_api_token: string;
  }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(cluster),
    });
    return this.handleResponse(response);
  }

  async updateCluster(id: number, updates: Record<string, any>): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/${id}`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
    });
    return this.handleResponse(response);
  }

  async deleteCluster(id: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/${id}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async testCluster(id: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/${id}/test`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async uploadClusterCaBundle(id: number, file: File): Promise<any> {
    const form = new FormData();
    form.append('file', file);
    // Don't set Content-Type — browser sets multipart boundary automatically
    const headers: Record<string, string> = {};
    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }
    const response = await fetch(`${this.baseUrl}/api/clusters/${id}/ca-bundle`, {
      method: 'POST',
      headers,
      body: form,
    });
    return this.handleResponse(response);
  }

  async deleteClusterCaBundle(id: number): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/${id}/ca-bundle`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async healthCheckAllClusters(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/health-check`, {
      method: 'POST',
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async getClusterRoutingConfig(): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/routing/config`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse(response);
  }

  async updateClusterRoutingConfig(mode: string, maxParallel = 5): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/clusters/routing/config`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ mode, max_parallel: maxParallel }),
    });
    return this.handleResponse(response);
  }

  // =================== Alert API Methods (Sprint 24) ===================

  async createAlertRule(body: {
    name: string;
    rule_kind?: 'metric' | 'slo';
    metric_query: string;
    condition?: string;
    threshold?: number;
    severity?: string;
    notification_channels?: Record<string, any>;
    evaluation_interval?: number;
    description?: string;
    entity_selector?: string;
  }): Promise<AlertRuleResponse> {
    const response = await fetch(`${this.baseUrl}/api/alerts/rules`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<AlertRuleResponse>(response);
  }

  async listAlertRules(): Promise<AlertRuleResponse[]> {
    const response = await fetch(`${this.baseUrl}/api/alerts/rules`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AlertRuleResponse[]>(response);
  }

  async getAlertRule(id: number): Promise<AlertRuleResponse> {
    const response = await fetch(`${this.baseUrl}/api/alerts/rules/${id}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AlertRuleResponse>(response);
  }

  async updateAlertRule(id: number, body: {
    name?: string;
    rule_kind?: 'metric' | 'slo';
    metric_query?: string;
    condition?: string;
    threshold?: number;
    severity?: string;
    notification_channels?: Record<string, any>;
    evaluation_interval?: number;
    is_enabled?: boolean;
    description?: string;
    entity_selector?: string;
  }): Promise<AlertRuleResponse> {
    const response = await fetch(`${this.baseUrl}/api/alerts/rules/${id}`, {
      method: 'PUT',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    });
    return this.handleResponse<AlertRuleResponse>(response);
  }

  async deleteAlertRule(id: number): Promise<{ status: string; message: string }> {
    const response = await fetch(`${this.baseUrl}/api/alerts/rules/${id}`, {
      method: 'DELETE',
      headers: this.buildHeaders(),
    });
    return this.handleResponse<{ status: string; message: string }>(response);
  }

  async listAlertEvents(params?: {
    rule_id?: number;
    status?: string;
    severity?: string;
    since?: string;
    limit?: number;
  }): Promise<AlertEventResponse[]> {
    const searchParams = new URLSearchParams();
    if (params?.rule_id) searchParams.set('rule_id', String(params.rule_id));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.severity) searchParams.set('severity', params.severity);
    if (params?.since) searchParams.set('since', params.since);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    const qs = searchParams.toString();
    const response = await fetch(`${this.baseUrl}/api/alerts/events${qs ? '?' + qs : ''}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AlertEventResponse[]>(response);
  }

  async getAlertSummary(): Promise<AlertSummaryResponse> {
    const response = await fetch(`${this.baseUrl}/api/alerts/events/summary`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AlertSummaryResponse>(response);
  }

  async getAlertRuleEvents(ruleId: number, limit = 50): Promise<AlertEventResponse[]> {
    const response = await fetch(`${this.baseUrl}/api/alerts/rules/${ruleId}/events?limit=${limit}`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<AlertEventResponse[]>(response);
  }

  // =================== HITL Write-Confirmation API (STORY-165) ===================

  async getHitlPending(): Promise<HITLPendingResponse> {
    const response = await fetch(`${this.baseUrl}/api/hitl/pending`, {
      headers: this.buildHeaders(),
    });
    return this.handleResponse<HITLPendingResponse>(response);
  }

  async approveHitl(confirmationId: string): Promise<HITLApproveResponse> {
    const response = await fetch(`${this.baseUrl}/api/hitl/approve`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ confirmation_id: confirmationId }),
    });
    return this.handleResponse<HITLApproveResponse>(response);
  }

  async denyHitl(confirmationId: string): Promise<HITLDenyResponse> {
    const response = await fetch(`${this.baseUrl}/api/hitl/deny`, {
      method: 'POST',
      headers: this.buildHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ confirmation_id: confirmationId }),
    });
    return this.handleResponse<HITLDenyResponse>(response);
  }
}

export const apiClient = new ApiClient();